<template>
    <div>
        <div class="content">
            <div v-if="loading.connection">
                <bouncing-component></bouncing-component>
            </div>
            <div :style="{visibility: loading.connection ? 'hidden': 'visible'}">
                <div class="editor">
                    <div class="row">
                        <div class="col-10">
                            <codemirror v-model="query.query" :options="cmOptions"
                                        class="form-group">
                            </codemirror>

                            <div class="form-group">
                                <b-button variant="secondary mr-2" @click.prevent="getBack">
                                    <font-awesome-icon icon="arrow-left"/>
                                    Back
                                </b-button>
                                <b-button @click.prevent="save" variant="primary" :disabled="loading.save" class="mr-2">
                                    <font-awesome-icon icon="save" class="mr-2" />
                                    <span>Save</span>
                                    <b-spinner
                                        v-if="loading.save"
                                        small
                                        class="ml-2" />
                                </b-button>
                                <b-button type="submit" variant="success" :disabled="loading.run" @click.prevent="execute" class="mr-2">
                                    <font-awesome-icon icon="play" class="mr-2" />
                                    <span>Run</span>
                                    <b-spinner
                                        v-if="loading.run"
                                        small
                                        class="ml-2" />
                                </b-button>
                                <toggle-button @change="setLimit"
                                            :sync="true" :width="40" :value="query.limited === 1">
                                </toggle-button>
                                Limit 100
                            </div>

                            <div>
                                <success-component
                                        v-if="success.length > 0"
                                        :message="success">
                                </success-component>
                                <error-component
                                        v-if="exceptions.length > 0"
                                        v-bind="exceptions[0]">
                                </error-component>

                                <b-alert :show="dismissCountDown"
                                        dismissible
                                        variant="warning"
                                        @dismissed="setDissmissCountDown(0)"
                                        @dismiss-count-down="countDownChanged">
                                    {{ this.error }}
                                </b-alert>
                                <div v-if="results.length > 0">
                                    <b-navbar class="report-nav p-0 pl-1" toggleable="lg">
                                        <b-navbar-toggle target="nav_dropdown_export"></b-navbar-toggle>
                                        <b-collapse id="nav_dropdown_export" is-nav>
                                            <b-navbar-nav class="ml-auto mb-2">
                                                <b-nav-item-dropdown right>
                                                    <template slot="button-content">
                                                        <font-awesome-icon icon="download"/>
                                                        Export
                                                    </template>
                                                    <b-dropdown-item @click="exportData('csv')">CSV</b-dropdown-item>
                                                    <b-dropdown-item @click="exportData('xls')">XLS</b-dropdown-item>
                                                </b-nav-item-dropdown>
                                            </b-navbar-nav>
                                        </b-collapse>
                                    </b-navbar>
                                    <div class="results" style="max-height: 1000px; width: 100%; overflow: auto">
                                        <ag-grid-vue style="width: 100%; height: 600px;"
                                            class="ag-theme-balham"
                                            :gridOptions="gridOptions"
                                            :columnDefs="columnDefs"
                                            :defaultColDef="defaultColDef"
                                            :groupUseEntireRow="false"
                                            :pagination="true"
                                            :paginationPageSize="18"
                                            :domLayout="'autoHeight'"
                                            :rowData="results">
                                        </ag-grid-vue>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="col-2 toolbox" >
                            <b-form-group
                                    label="Connection"
                                    label-for="connection" class="panel-label">
                                <b-form-select class="custom-select mb-1" @input="setConnection" :value="query.connection_id"
                                               name="connection">
                                    <option :value="-1" selected disabled>
                                        Choose Connection
                                    </option>
                                    <option v-for="connection in connections"
                                            :value="connection.id"
                                            :key="connection.id">
                                        {{ connection.name }}
                                    </option>
                                </b-form-select>
                            </b-form-group >

                            <browse-tables v-if="!loading.connection" :connection="query.connection_id"></browse-tables>

                            <div class="panel-label">Parameters</div>

                            <div v-for="(param, index) in parameters" v-bind:key="index">
                                <query-parameter :index="index"></query-parameter>
                            </div>

                            <a href="#" class="addmetric btn-link" @click.prevent="addParameter()">
                                <font-awesome-icon icon="plus-circle" /> Add parameter
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import 'codemirror/mode/sql/sql.js'
    import 'codemirror/theme/monokai.css'
    import ToggleButton from 'vue-js-toggle-button/src/Button';

    import ErrorComponent from '../../components/ErrorView.vue';
    import SuccessComponent from '../../components/Success.vue';
    import QueryParameter from '../../components/QueryParameter';
    import HeaderComponent from './Header.vue';
    import {mapState, mapMutations, mapActions} from 'vuex';
    import BouncingComponent from '../../components/Bouncing-loader.vue';
    import BrowseTables from '../../components/BrowseTables.vue';
    import {AgGridVue} from "ag-grid-vue";

    export default {
        props: {
            query_id: {
                required: false
            },
            queryData: {
                type: String
            }
        },
        components: {
            SuccessComponent,
            ErrorComponent,
            QueryParameter,
            ToggleButton,
            HeaderComponent,
            BouncingComponent,
            BrowseTables,
            AgGridVue,
        },
        data() {
            return {
                code: '',
                cmOptions: {
                    tabSize: 4,
                    mode: 'text/x-mysql',
                    theme: 'monokai',
                    lineNumbers: true,
                    line: true,
                },
                loading: {
                  connection: false,
                  save: false,
                  run: false,
                },
                unwatch: '',
                gridOptions: {},
                defaultColDef: {
                    filter: true,
                    sortable: true,
                    rowDraw: true,
                    enableValue: true,
                    enableRowGroup: true,
                    enablePivot: true,
                    resizable: true,
                }
            }
        },
        methods: {
            ...mapMutations('queries', {
                setDissmissCountDown: 'SET_DISSMISS_COUNTDOWN',
                setConnectionSelected: 'SET_CONNECTION_SELECTED'
            }),
            ...mapActions('queries', {
                executeQuery: 'execute'
            }),
            addParameter() {
                this.$store.dispatch('queries/addparameter');
            },
            countDownChanged(dismissCountDown) {
                this.setDissmissCountDown(dismissCountDown);
            },
            execute: function () {
                this.loading.run = true;
                this.executeQuery(this.connectionId).then(() => {
                    this.loading.run = false;
                });
            },
            getBack() {
                this.$router.go(-1);
            },
            save() {
                this.loading.save = true;
                this.$store.dispatch('queries/update').then(() => {
                    this.loading.save = false;
                });
            },
            setConnection(val) {
                if (val !== -1) {
                    this.setConnectionSelected(val);

                    if (!this.query_id) {
                        let params = {
                            connection_id: val,
                            organization_id: parseInt(this.$store.state.organizationSelected),
                            query: this.query.data,
                            status: 0
                        };
                        this.createQuery(params);
                    }
                }
            },
            setLimit(val) {
                let status = 0;
                if (val.value) {
                    status = 1;
                }
                this.$store.commit('queries/SET_QUERY_LIMIT', status);
            },
            setDefaultConnection() {
                if (!this.query_id && this.connections.length > 0) {
                    this.$store.commit('queries/SET_CONNECTION_SELECTED', this.connections[0].id);
                }
            },
            createQuery(params) {
                return new Promise(resolve => {
                    this.$store.dispatch('queries/create', params).then(res => {
                        let queryId = res.id;
                        this.$router.replace({name: 'query.edit', params: {query_id: queryId}});
                        resolve();
                    });
                })
            },
            newQuery() {
                if (this.connections.length > 0) {
                    this.loading.connection = true;
                    this.setConnectionSelected(this.connections[0].id);
                    let params = {
                        connection_id: this.connections[0].id,
                        query: this.query.data,
                        status: 0
                    };
                    this.createQuery(params).then(() => {
                        this.loading.connection = false;
                    });
                }
            },
            exportData(type) {
                let params = {
                    fileName: 'table',
                    columnSeparator: ','
                };
                if (type === 'csv') {
                    this.gridOptions.api.exportDataAsCsv(params);
                }
                else if (type === 'xls') {
                    this.gridOptions.api.exportDataAsExcel(params);
                }
            },
        },
        computed: {
            ...mapState('connections', {
                connections: 'items'
            }),
            ...mapState('queries', {
                query: 'query',
                results: 'results',
                error: 'executionError',
                dismissCountDown: 'dismissCountDown',
                connectionId: 'connectionId',
                parameters: 'parameters',
                limitQuery: 'limitQuery',
            }),
            ...mapState({
                success: 'success',
                exceptions: 'exceptions',
            }),
            codemirror() {
                return this.$refs.myCm.codemirror
            },
            stringData() {
                return this.results;
            },
            columnDefs() {
                const columsDefinition = [];
                if (this.results[0]) {
                    let keys = Object.keys(this.results[0]);
                    keys.forEach(key => {
                        columsDefinition.push(
                            {
                                headerName: key,
                                field: key,
                                enableValue: true,
                            }
                        )
                    });
                }
                return columsDefinition;
            }
        },
        created() {
            this.loading.connection = true;
            this.$store.dispatch('connections/fetchAll').then(() => {
                if (!this.query_id) {
                    this.loading.connection = false;
                }
                this.setDefaultConnection();
            });
            if (this.query_id) {
                this.$store.dispatch('queries/setQuery', this.query_id).then(res => {
                    // To use the query from the History
                    if (this.$store.state.queries.flagForHistory) {
                        this.$store.commit('queries/SET_QUERY_DATA', this.$store.state.queries.historyData);
                    }
                    this.loading.connection = false;
                });
            }
            this.unwatch = this.$store.watch(
                (state)=>
                    this.$store.state.organizationSelected
                ,
                (newValue, oldValue)=>{
                    this.loading.connection = true;
                    this.$store.dispatch('queries/setInitialStatus').then(() => {
                        this.loading.connection = false;
                    });
                    this.$store.dispatch('clearMessages');
                    this.$store.dispatch('connections/fetchAll').then((res) => {
                        this.setDefaultConnection();
                    });
                    this.$router.replace({ name: 'query.create' });
                },
            );
        },
        beforeDestroy() {
            this.$store.commit('queries/SET_HISTORY_FLAG', false);
            this.$store.commit('queries/SET_HISTORY_DATA', '');
            this.$store.dispatch('clearMessages');
            this.unwatch();
        },
    }
</script>

<style scoped>
    .nav-tabs .nav-link.active, .nav-tabs .nav-item.show .nav-link {
        background-color: #ffffff;
        border-bottom: solid 1px #ffffff;
    }

    .addmetric:active, .addmetric:focus {
        color: #007bff;
    }

    .panel-label {
        font-weight: bold;
        margin-bottom: 5px;
    }
</style>
